import React, {useEffect, useState} from 'react';
import './Profile.css';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormLabel,
    TextField,
    Typography,
    Grid,
    Card,
    CardContent,
    Stack,
    Divider,
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import UploadAvatar from './Components/UploadAvatar';
import {updateGuestProfile, getGuestProfile} from '../../store/setProfile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WebcamCapture from './Components/WebcamCapture';
import {checkAndRequestCameraPermission} from '../../utils/video-recorder';
import {toast} from 'react-toastify';

const GuestProfile = () => {
    const { firstName, lastName } = useSelector(
        (state) => state.main
    )
    const { phone, avatar, tempAvatar } = useSelector(
        (state) => state.profile.guestInformation
    )
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [showCropper, setShowCropper] = useState(true)
    const [openCaptureDialog, setOpenCaptureDialog] = React.useState(false);

    const [user, setUser] = useState({
        firstName: firstName || '',
        lastName: lastName || '',
        phone: phone || '',
    });

    // Fetch guest profile data on mount to ensure phone is loaded
    useEffect(() => {
        dispatch(getGuestProfile());
    }, [dispatch])

    // Update user state when Redux values change
    useEffect(() => {
        setUser({
            firstName: firstName || '',
            lastName: lastName || '',
            phone: phone || '',
        });
    }, [firstName, lastName, phone])

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const data = {
            phone: user.phone,
            avatar: tempAvatar,
            firstName: user.firstName,
            lastName: user.lastName
        }
        dispatch(updateGuestProfile({data, setLoading, setShowCropper}))
    }

    const handleOpenImageCapture = async () => {
        if(!(await checkAndRequestCameraPermission())) {
            toast.error('Camera permission denied. Please allow camera permission to capture photo.')
            return
        }
        setOpenCaptureDialog(true)
    }
    const handleCloseImageCapture = () => {
        setOpenCaptureDialog(false)
    }

    return (
        <Box sx={{ px: 2 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
                Update Profile
            </Typography>
            
            <Card 
                sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    boxShadow: 'none',
                    mb: 3
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box
                        component="form"
                        id="guest-profile-form"
                        onSubmit={handleSubmit}
                    >
                        <Grid container spacing={4}>
                            {/* Upload Photo Section */}
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Box className="image-input" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <UploadAvatar showCropper={showCropper} />
                                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                size="small"
                                                onClick={handleOpenImageCapture}
                                                endIcon={<PhotoCameraIcon />}
                                                sx={{
                                                    borderColor: '#712d94',
                                                    color: '#712d94',
                                                    '&:hover': {
                                                        borderColor: '#5a2470',
                                                        backgroundColor: 'rgba(113, 45, 148, 0.04)'
                                                    }
                                                }}
                                            >
                                                Capture Photo
                                            </Button>
                                        </Box>
                                        {openCaptureDialog && (
                                            <WebcamCapture 
                                                open={openCaptureDialog} 
                                                handleClose={handleCloseImageCapture}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <Divider sx={{ mt: 1 }} />
                            </Grid>

                            {/* Personal Information Section */}
                            <Grid item xs={12}>
                                <Typography 
                                    variant="h6" 
                                    component="h2" 
                                    sx={{ 
                                        mb: 3, 
                                        fontWeight: 600,
                                        fontSize: '1.2rem',
                                        color: 'text.primary'
                                    }}
                                >
                                    Personal Information
                                </Typography>
                            </Grid>

                            {/* First Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    type="text"
                                    value={user?.firstName || ''}
                                    onChange={(e) =>
                                        setUser({...user, firstName: e.target.value})
                                    }
                                    variant="outlined"
                                />
                            </Grid>

                            {/* Last Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    type="text"
                                    value={user?.lastName || ''}
                                    onChange={(e) =>
                                        setUser({...user, lastName: e.target.value})
                                    }
                                    variant="outlined"
                                />
                            </Grid>

                            {/* Phone */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    type="number"
                                    value={user?.phone || ''}
                                    onKeyDown={e => (e.keyCode === 69 || e.keyCode === 190) && e.preventDefault()}
                                    onChange={(e) => setUser({...user, phone: e.target.value})}
                                    variant="outlined"
                                />
                            </Grid>

                            {/* Update Button */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        type="submit"
                                        disabled={loading}
                                        sx={{
                                            minWidth: '120px',
                                            backgroundColor: '#712d94',
                                            '&:hover': {
                                                backgroundColor: '#5a2470',
                                            },
                                            '&:disabled': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                color: 'rgba(0, 0, 0, 0.26)',
                                            }
                                        }}
                                    >
                                        {loading && (
                                            <CircularProgress 
                                                size="20px" 
                                                sx={{ 
                                                    color: "white", 
                                                    mr: 1 
                                                }} 
                                            />
                                        )}
                                        Update
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default GuestProfile;
